'use client';

import { createTranslator } from 'next-intl';

import { DEFAULT_LOCALE } from './config';
import enMessages from './en-fallback.json';

/**
 * Loosely typed on purpose: callers pass runtime key strings, so the catalogue's
 * literal key union would only get in the way here.
 */
type LooseTranslator = (key: string, values?: Record<string, string | number | Date>) => string;

/**
 * Escape hatch for code that must produce user-facing copy but cannot call
 * `useTranslations` — plain modules that fire toasts (wallet connectors, swap
 * broadcast helpers) and run outside any React render.
 *
 * `ClientTranslatorBridge` keeps this in sync with the active locale. Until it
 * mounts we fall back to the shared English catalogue, so a toast fired during early
 * boot still renders real copy rather than a key.
 *
 * Prefer `useTranslations` anywhere a hook is legal — this exists for the cases
 * where it isn't.
 */
let translator: LooseTranslator | null = null;
let fallback: LooseTranslator | null = null;

export function registerClientTranslator(next: unknown) {
  translator = next as LooseTranslator;
}

/**
 * Built on first use rather than at module scope: this module is imported by
 * plain modules that sit inside pre-existing import cycles, and evaluating
 * `createTranslator` during module init crashes those cycles with a TDZ error.
 */
function getFallback(): LooseTranslator {
  if (!fallback) {
    fallback = createTranslator({ locale: DEFAULT_LOCALE, messages: enMessages }) as unknown as LooseTranslator;
  }

  return fallback;
}

export function translateOutsideReact(key: string, values?: Record<string, string | number | Date>): string {
  return (translator ?? getFallback())(key, values);
}
